import React, { useState, useEffect } from 'react';
import { Building2, Shield, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Checkbox } from '@vaiu/ui/checkbox';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { PaymentMethod, platformApi } from '@/services/platformApi';
import { usePlaidLink } from 'react-plaid-link';
import { useConfig } from '@/context/ConfigContext';

interface PlaidBankFormProps {
  onSubmit: (method: PaymentMethod) => Promise<void>;
  loading: boolean;
  onCancel: () => void;
}

const PlaidBankForm: React.FC<PlaidBankFormProps> = ({
  onSubmit,
  loading,
  onCancel
}) => {
  const { config: appConfig, loading: configLoading } = useConfig();
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [plaidConfig, setPlaidConfig] = useState<any>(null);
  const [initializingPlaid, setInitializingPlaid] = useState(true);
  const [plaidIsOpen, setPlaidIsOpen] = useState(false);

  // Add CSS class to body when Plaid is open to handle modal conflicts
  useEffect(() => {
    if (plaidIsOpen) {
      document.body.classList.add('plaid-modal-open');
      // Temporarily increase z-index of parent dialog
      const dialogOverlay = document.querySelector('[data-radix-dialog-overlay]') as HTMLElement;
      const dialogContent = document.querySelector('[data-radix-dialog-content]') as HTMLElement;
      
      if (dialogOverlay) {
        dialogOverlay.style.zIndex = '9997';
      }
      if (dialogContent) {
        dialogContent.style.zIndex = '9998';
      }
    } else {
      document.body.classList.remove('plaid-modal-open');
    }

    return () => {
      document.body.classList.remove('plaid-modal-open');
    };
  }, [plaidIsOpen]);

  // Initialize Plaid configuration and get link token
  useEffect(() => {
    const initializePlaid = async () => {
      try {
        setInitializingPlaid(true);
        setError(null);

        console.log('🔄 Initializing Plaid...');
        
        // Check app config first - if disabled in app config, error immediately
        const plaidEnabledInApp = appConfig?.integrations?.plaidEnabled ?? true;
        if (!plaidEnabledInApp) {
          throw new Error('Plaid integration is not enabled');
        }
        
        // Get Plaid configuration
        const config = await platformApi.getPlaidConfig();
        console.log('📋 Plaid config:', config);
        
        // If app config says enabled but Plaid config says disabled, 
        // trust app config (backend may not be synced) but log a warning
        if (!config.enabled) {
          console.warn('⚠️ Plaid config says disabled, but app config says enabled. Proceeding based on app config.');
        }
        
        if (!config.clientId) {
          throw new Error('Plaid client ID not configured');
        }
        
        setPlaidConfig(config);
        
        // Get link token
        console.log('🔗 Creating Plaid link token...');
        const { linkToken: token } = await platformApi.createPlaidLinkToken();
        setLinkToken(token);
        
        console.log('✅ Plaid initialized successfully');
      } catch (err: any) {
        console.error('❌ Failed to initialize Plaid:', err);
        setError(err.message || 'Failed to initialize Plaid Link');
      } finally {
        setInitializingPlaid(false);
      }
    };

    // Only initialize if app config is loaded
    if (appConfig) {
      initializePlaid();
    }
  }, [appConfig]);

  const handlePlaidSuccess = async (publicToken: string, metadata: any) => {
    try {
      console.log('🏦 Plaid Link success:', { publicToken, metadata });
      setPlaidIsOpen(false); // Close Plaid modal state
      
      // Extract account data from metadata
      const accountData = {
        accountName: metadata.accounts?.[0]?.name || metadata.institution?.name || 'Bank Account',
        accountType: metadata.accounts?.[0]?.subtype || 'checking',
        last4: metadata.accounts?.[0]?.mask || '****',
        institutionName: metadata.institution?.name,
        institutionId: metadata.institution?.institution_id
      };
      
      // Exchange public token for bank account details - this saves to backend
      const savedPaymentMethod = await platformApi.exchangePlaidPublicToken(publicToken, accountData);
      
      // Update the isDefault preference if needed
      if (setAsDefault && !savedPaymentMethod.isDefault) {
        // Set as default via API call
        await platformApi.setDefaultPaymentMethod(savedPaymentMethod.id!);
        savedPaymentMethod.isDefault = true;
      }

      // Pass the actual saved payment method to parent
      await onSubmit(savedPaymentMethod);
    } catch (err: any) {
      console.error('❌ Failed to process bank account:', err);
      setError(err.message || 'Failed to connect bank account');
    }
  };

  const handlePlaidExit = (err: any, metadata: any) => {
    console.log('🚪 Plaid Link exit:', { err, metadata });
    setPlaidIsOpen(false); // Close Plaid modal state
    if (err) {
      setError(err.display_message || err.error_message || 'Bank connection was cancelled');
    }
  };

  const handleConnectClick = () => {
    setPlaidIsOpen(true); // Open Plaid modal state
    open();
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
    env: plaidConfig?.environment || 'sandbox',
  });

  if (configLoading || initializingPlaid) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Setting up bank connection...</span>
      </div>
    );
  }

  if (error && !linkToken) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="flex space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">How it works:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Securely connect your bank account through Plaid</li>
            <li>• We'll verify your account for ACH transfers</li>
            <li>• Your login credentials are never stored</li>
            <li>• Bank-level security and encryption</li>
          </ul>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="setAsDefault"
            checked={setAsDefault}
            onCheckedChange={(checked) => setSetAsDefault(checked as boolean)}
          />
          <Label htmlFor="setAsDefault" className="text-sm">
            Set as default payment method
          </Label>
        </div>

        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Shield className="w-4 h-4" />
          <span>Bank-grade security powered by Plaid</span>
        </div>

        <Button
          onClick={handleConnectClick}
          disabled={loading || !ready || !linkToken}
          className="w-full"
        >
          {!ready ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <Building2 className="w-4 h-4 mr-2" />
              Connect Bank Account
              <ExternalLink className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>

        <div className="text-xs text-muted-foreground text-center">
          By connecting your bank account, you agree to Plaid's{' '}
          <a href="https://plaid.com/legal/#privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://plaid.com/legal/#terms-of-service" target="_blank" rel="noopener noreferrer" className="underline">
            Terms of Service
          </a>
        </div>
      </div>

      <div className="flex space-x-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default PlaidBankForm; 